import { Player, Bid } from '../types';

/**
 * Get player display name with emoji
 */
export const getPlayerDisplayName = (playerName: string, players: Player[]): string => {
  const player = players.find(p => p.name === playerName);
  return player?.emoji ? `${player.emoji} ${playerName}` : playerName;
};

/**
 * Get player display by ID with emoji
 */
export const getPlayerDisplayById = (playerId: string, players: Player[]): string => {
  const player = players.find(p => p.id === playerId);
  if (player?.emoji) {
    return `${player.emoji} ${player.name}`;
  }
  return player?.name || playerId;
};

/**
 * Check if a bid is valid given current game state
 */
export const isBidValid = (
  quantity: number,
  faceValue: number,
  currentBid: Bid | null
): boolean => {
  if (!currentBid) return true;

  if (quantity > currentBid.quantity) return true;
  if (quantity === currentBid.quantity && faceValue > currentBid.faceValue) return true;

  return false;
};

/**
 * Get the last bid made by a specific player
 */
export const getPlayerLastBid = (playerId: string, bidHistory: Bid[]): Bid | null => {
  const playerBids = bidHistory.filter(bid => bid.playerId === playerId);
  return playerBids.length > 0 ? playerBids[playerBids.length - 1] : null;
};

/**
 * Format a bid for display
 */
export const formatBid = (quantity: number, faceValue: number): string => {
  return `${quantity}×${faceValue}`;
};

/**
 * Get player status emoji (eliminated, current emoji)
 */
export const getPlayerStatusEmoji = (player: Player, isEliminated: boolean): string => {
  return isEliminated ? '☠️' : (player.emoji || '👤');
};

/**
 * Determine connection status color class
 */
export const getConnectionStatusColor = (player: Player, isWaiting: boolean = false): string => {
  if (!player.isConnected) return 'bg-red-500';

  if(player.isAI) {
    return player.aiFallback ? 'bg-yellow-500' : 'bg-green-500';
  }

  // In waiting phase, only care about ready status
  if (isWaiting) {
    return player.ready ? 'bg-green-500' : 'bg-yellow-500';
  }

  // During gameplay, show yellow for AI fallback
  if (player.aiFallback) return 'bg-yellow-500';
  return 'bg-green-500';
};